import { useEffect, useMemo, useRef, useState } from 'react'
import { api, type Kind, type Skill } from '../api'

export interface PaletteResult {
  type: 'view' | 'skill' | 'kind'
  label: string
  sub?: string
  tab: string
  query?: string
}

// CommandPalette is the global search (⌘/Ctrl-K): one box that finds any view,
// skill, or kind and jumps straight to it — the cross-view cohesion the flat tabs
// lacked. Skills/kinds open their view pre-filtered to the match.
export function CommandPalette({
  views,
  onSelect,
  onClose,
}: {
  views: { id: string; label: string }[]
  onSelect: (r: PaletteResult) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [skills, setSkills] = useState<Skill[]>([])
  const [kinds, setKinds] = useState<Kind[]>([])
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    api
      .skills()
      .then((s) => setSkills(s ?? []))
      .catch(() => {})
    api
      .kinds()
      .then((k) => setKinds(k ?? []))
      .catch(() => {})
  }, [])

  const results = useMemo<PaletteResult[]>(() => {
    const q = query.trim().toLowerCase()
    const viewResults: PaletteResult[] = views
      .filter((v) => !q || v.label.toLowerCase().includes(q))
      .map((v) => ({ type: 'view', label: v.label, tab: v.id }))
    if (!q) return viewResults
    const skillResults: PaletteResult[] = skills
      .filter(
        (s) =>
          s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q),
      )
      .slice(0, 6)
      .map((s) => ({ type: 'skill', label: s.name, sub: s.category, tab: 'skills', query: s.name }))
    const kindResults: PaletteResult[] = kinds
      .filter(
        (k) =>
          k.name.toLowerCase().includes(q) || (k.description ?? '').toLowerCase().includes(q),
      )
      .slice(0, 6)
      .map((k) => ({ type: 'kind', label: k.name, sub: k.blast_radius, tab: 'kinds', query: k.name }))
    return [...viewResults, ...skillResults, ...kindResults]
  }, [query, skills, kinds, views])

  useEffect(() => {
    setActive(0)
  }, [query])

  return (
    <div className="palette-overlay" onClick={onClose}>
      <div className="palette" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Search">
        <input
          ref={inputRef}
          className="palette-input"
          placeholder="Search views, skills, kinds…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActive((a) => Math.min(a + 1, results.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActive((a) => Math.max(a - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              const r = results[active]
              if (r) onSelect(r)
            } else if (e.key === 'Escape') {
              onClose()
            }
          }}
        />
        <ul className="palette-results">
          {results.length === 0 ? (
            <li className="palette-empty">No matches</li>
          ) : (
            results.map((r, i) => (
              <li
                key={r.type + ':' + r.label}
                className={`palette-item ${i === active ? 'active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => onSelect(r)}
              >
                <span className={`palette-type ${r.type}`}>{r.type}</span>
                <span className="palette-label">{r.label}</span>
                {r.sub && <span className="palette-sub">{r.sub}</span>}
              </li>
            ))
          )}
        </ul>
        <div className="palette-hint">↑↓ navigate · ↵ open · esc close</div>
      </div>
    </div>
  )
}
